import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Building2, Mail, Phone, Globe, MapPin, Power } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeactivatedDealershipsPage() {
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reactivating, setReactivating] = useState(null);
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchDeactivated();
  }, []);

  const fetchDeactivated = async () => {
    try {
      const res = await axios.get(`${API}/dealerships?active=false`, { headers, withCredentials: true });
      setDealerships(res.data);
    } catch (err) { console.error('Failed to fetch deactivated dealerships:', err); }
    setLoading(false);
  };

  const handleReactivate = async (id, name) => {
    setReactivating(id);
    try {
      await axios.put(`${API}/dealerships/${id}/reactivate`, {}, { headers, withCredentials: true });
      toast.success(`${name} reactivated`);
      fetchDeactivated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    }
    setReactivating(null);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="deactivated-dealerships-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
          Deactivated
        </h1>
        <p className="text-slate-400 text-sm mt-2">Dealerships that are no longer processing leads</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : dealerships.length === 0 ? (
        <div className="text-center py-20" data-testid="no-deactivated">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" strokeWidth={1} />
          <p className="text-slate-500 text-sm">No deactivated dealerships</p>
        </div>
      ) : (
        <div className="columns-1 lg:columns-2 gap-6 space-y-6" data-testid="deactivated-grid">
          {dealerships.map(dlr => (
            <Card
              key={dlr.dealership_id}
              className="bg-[#0A0A0A] border-[#262626] opacity-70 hover:opacity-100 transition-all duration-300 break-inside-avoid"
              data-testid={`deactivated-card-${dlr.dealership_id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-600/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-slate-400 uppercase tracking-wider">{dlr.name}</h3>
                      {dlr.brands && dlr.brands.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {dlr.brands.map(brand => (
                            <Badge key={brand} className="bg-slate-500/10 text-slate-500 border border-slate-500/20 text-[8px] font-bold tracking-wider uppercase">
                              {brand}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleReactivate(dlr.dealership_id, dlr.name)}
                    disabled={reactivating === dlr.dealership_id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] tracking-widest uppercase"
                    data-testid={`reactivate-dlr-${dlr.dealership_id}`}
                  >
                    <Power className="w-3.5 h-3.5 mr-1.5" />
                    {reactivating === dlr.dealership_id ? 'Reactivating...' : 'Reactivate'}
                  </Button>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {dlr.email_alias && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="font-mono text-xs">{dlr.email_alias}</span>
                    </div>
                  )}
                  {dlr.phone && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Phone className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-xs">{dlr.phone}</span>
                    </div>
                  )}
                  {dlr.website && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Globe className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-xs">{dlr.website}</span>
                    </div>
                  )}
                  {(dlr.city || dlr.state) && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <MapPin className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-xs">{[dlr.city, dlr.state].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>

                {dlr.deactivated_at && (
                  <p className="text-[10px] text-slate-600 mt-3 uppercase tracking-wider">
                    Deactivated {new Date(dlr.deactivated_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
